import { referenceMonthOf } from '../../shared/date/reference-month.js'
import type { ReferenceMonth } from '../../shared/date/reference-month.js'
import { IN_COMPETENCE } from './cash-impact.js'
import type { SpendEntry } from './cash-impact.js'
import type { Category } from './category.js'
import type { Transaction } from './transaction.js'

/**
 * As duas visões do mês, e a lista de `SpendEntry` que alimenta `totalSpend`.
 * Transcrição executável da segunda metade de
 * `Docs/03-Arquitetura/Competência e caixa.md` — a primeira é `cash-impact.ts`,
 * que classifica cada transação; esta aqui recorta o mês.
 *
 * **Por que este arquivo existe, e não é a consulta do Firestore.** A Task 8
 * escreve o recorte duas vezes: uma como `where`/`orderBy` em
 * `adapters/firebase/transaction-repository.ts`, outra em memória. Duas
 * implementações do modelo central da fase, sem nada comparando uma com a
 * outra, é como o modelo diverge de si mesmo sem ninguém ficar sabendo — foi o
 * achado da revisão da Task 7. Com o predicado aqui:
 *
 * - `accrual-and-cash.spec.ts`, a definição de pronto da fase, o exercita nas
 *   nove linhas dela, e é ele que sai errado se o modelo sair errado;
 * - `tests/adapters/finance-repositories.test.ts` roda a consulta de verdade
 *   contra o emulador e compara o resultado com este predicado sobre o mesmo
 *   livro. A consulta deixa de ser uma segunda opinião sobre o modelo e passa a
 *   ser uma implementação verificada contra ele.
 *
 * As duas visões **não** particionam o livro, e a tabela da nota mostra por
 * quê: `direct` é ✅ nas duas colunas, então um Pix de 05/07 está na
 * competência de julho **e** no caixa de julho. Isso não conta nada duas vezes
 * — é um movimento só, e as duas perguntas são diferentes com a mesma resposta.
 * O que o modelo promete é mais estreito: `deferred` e `settlement` nunca caem
 * na mesma visão, então a compra no cartão e o pagamento da fatura dela não se
 * encontram em lugar nenhum. Ver a decisão 96(b).
 */

/**
 * A **coluna de competência** da tabela da nota, uma entrada por linha.
 *
 * `Record<CashImpact, boolean>` e não um `!== 'settlement'` solto: a união é
 * fechada, então um membro novo em `Transaction['cashImpact']` **não compila**
 * até alguém dizer o que ele faz nesta coluna. Um `!==` aceitaria o membro novo
 * calado, na direção que infla o número mais visível do produto.
 *
 * Não existe o mapa irmão para a coluna de caixa, e a ausência é o desenho:
 * o caixa **não** filtra por `cashImpact`. Ele recorta por `settlementDate`, e
 * `deferred` cai fora sozinho porque `deriveSettlementDate` lhe dá `null`
 * (decisão 94). Um segundo filtro ali seria uma segunda fonte de verdade para
 * um fato que já está no dado. Quem prende a coluna de caixa da tabela é
 * `monthly-view.test.ts`, comparando o comportamento com a tabela escrita lá.
 */
/**
 * `IN_COMPETENCE` e `COMPETENCE_IMPACTS` **mudaram de casa** em 2026-08-31, para
 * `cash-impact.ts`, e a mudança conserta uma divergência real.
 *
 * `totalSpend` filtrava `cashImpact !== 'settlement'` por conta própria — a
 * mesma pergunta que este mapa responde, escrita duas vezes. E as duas
 * **discordavam**: quando `internal` entrou (decisão 138), o mapa o excluiu e o
 * filtro do `totalSpend` não, porque ele nomeava um valor em vez de perguntar.
 *
 * Não havia bug vivo — a lista de competência já vinha filtrada —, mas passar
 * uma entrada `internal` direto para `totalSpend` a somaria. Agora há um lugar
 * só, e ele fica ao lado do tipo que governa.
 */


/**
 * Competência — *isto foi consumo deste mês?* Recorta por `transactionDate`, a
 * data da **compra**, e deixa `settlement` de fora: pagar a fatura não é
 * consumir, e o consumo já foi contado quando cada compra entrou.
 *
 * `transactionDate` e não `postedAt`: o lançamento pela instituição serve para
 * exibição e conciliação, e é tipicamente um ou dois dias depois — o bastante
 * para jogar uma compra do fim do mês para o mês seguinte.
 */
export function isInCompetence(transaction: Transaction, month: ReferenceMonth): boolean {
  return (
    IN_COMPETENCE[transaction.cashImpact] &&
    referenceMonthOf(transaction.source.transactionDate) === month
  )
}

/**
 * Caixa — *isto saiu da conta neste mês?* Recorta por `settlementDate`, e
 * `settlementDate` nulo nunca entra: é assim que a compra no cartão fica de
 * fora sem precisar de um segundo filtro, porque `deferred` tem `null` ali para
 * sempre.
 */
export function isInCash(transaction: Transaction, month: ReferenceMonth): boolean {
  return (
    transaction.settlementDate !== null &&
    referenceMonthOf(transaction.settlementDate) === month
  )
}

/**
 * A lista de `SpendEntry` de um conjunto de transações — o montador que faltava
 * para `totalSpend` ter de onde ser chamado, e o único do repositório.
 *
 * **`categoryKind` ausente vira `null`, e `null` conta como gasto** (decisão
 * 96a). São dois caminhos diferentes para o mesmo `null`, e os dois são
 * comuns:
 *
 * 1. `enrichment.categoryId` é `null` — o estado de toda transação
 *    recém-sincronizada, antes de alguém a categorizar;
 * 2. `enrichment.categoryId` aponta para uma categoria que **não está na
 *    lista** — categoria criada pelo usuário e depois apagada, que
 *    `firestore.rules` permite (decisão 100) e que o `enrichment` de transação
 *    antiga continua citando.
 *
 * O `?? null` do segundo caso é a guarda que este arquivo tem de mais delicado.
 * Qualquer default no lugar dele — e `'transfer'` é o que mais parece razoável,
 * porque "não sei o que é" soa como "não é gasto" — some com o dinheiro de todo
 * relatório, calado. Com `null`, a transação não categorizada aparece como
 * gasto até que se diga o contrário: o número sai grande demais em vez de
 * pequeno demais, que é o erro que alguém percebe.
 *
 * **Transação `canceled` não entra na lista.** `transaction.ts` diz que ela sai
 * de todas as somas e não é apagada, e este é o único lugar do repositório onde
 * uma soma de gasto é montada — em nenhum outro havia onde pôr a regra. As duas
 * consultas continuam **devolvendo** a transação cancelada: ela é parte do
 * extrato do mês e a tela mostra que existiu; o que ela não é, é gasto.
 */
export function spendEntriesOf(
  transactions: Transaction[],
  categories: Category[],
): SpendEntry[] {
  const kindById = new Map(categories.map((category) => [category.id, category.kind]))

  return transactions
    .filter((transaction) => transaction.status !== 'canceled')
    .map((transaction) => ({
      cashImpact: transaction.cashImpact,
      amountCents: transaction.source.amountCents,
      categoryKind:
        transaction.enrichment.categoryId === null
          ? null
          : (kindById.get(transaction.enrichment.categoryId) ?? null),
      offsetCents: transaction.enrichment.offset?.offsetCents ?? 0,
      absorbed: transaction.enrichment.offsetOf !== null,
    }))
}
